import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { supabase } from '@/lib/supabase'
import { useProject } from '@/contexts/project-context'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Loader2 } from 'lucide-react'

const scenarioSchema = z.object({
  scenario_name: z.string().min(1, 'Please enter a scenario name'),
  scenario_type: z.enum(['acquisition', 'ipo', 'liquidation', 'merger']),
  exit_valuation: z.number().min(1, 'Please enter an exit valuation'),
  exit_date: z.string().optional(),
  assumptions: z.string().optional(),
})

type ScenarioFormData = z.infer<typeof scenarioSchema>

interface ExitScenarioFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

export default function ExitScenarioForm({ 
  open, 
  onOpenChange, 
  onSuccess 
}: ExitScenarioFormProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { currentProject } = useProject()

  const form = useForm<ScenarioFormData>({
    resolver: zodResolver(scenarioSchema),
    defaultValues: {
      scenario_name: '',
      scenario_type: 'acquisition',
      exit_valuation: 0,
      exit_date: new Date().toISOString().split('T')[0],
      assumptions: '',
    },
  })

  const onSubmit = async (data: ScenarioFormData) => {
    setLoading(true)
    setError(null)

    try {
      if (!currentProject) {
        throw new Error('No project selected')
      }

      // Parse assumptions into JSON if provided
      let assumptionsJson = {}
      if (data.assumptions) {
        try {
          // Try to parse as JSON first
          assumptionsJson = JSON.parse(data.assumptions)
        } catch {
          // If not valid JSON, store as text in an object
          assumptionsJson = { notes: data.assumptions }
        }
      }

      const { error } = await supabase
        .from('exit_scenarios')
        .insert({
          project_id: currentProject.id,
          scenario_name: data.scenario_name,
          scenario_type: data.scenario_type,
          exit_valuation: data.exit_valuation,
          exit_date: data.exit_date || null,
          assumptions: assumptionsJson,
          created_by: (await supabase.auth.getUser()).data.user?.id,
        })

      if (error) throw error

      onSuccess?.()
      onOpenChange(false)
      form.reset()
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create Exit Scenario</DialogTitle>
          <DialogDescription>
            Model a potential exit event to calculate payout distributions
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="scenario_name">Scenario Name</Label>
            <Input
              id="scenario_name"
              {...form.register('scenario_name')}
              placeholder="e.g., Series B Acquisition, IPO 2025"
            />
            {form.formState.errors.scenario_name && (
              <p className="text-sm text-red-600">{form.formState.errors.scenario_name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="scenario_type">Scenario Type</Label>
            <Select
              value={form.watch('scenario_type')}
              onValueChange={(value) => form.setValue('scenario_type', value as any)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="acquisition">Acquisition</SelectItem>
                <SelectItem value="ipo">IPO</SelectItem>
                <SelectItem value="merger">Merger</SelectItem>
                <SelectItem value="liquidation">Liquidation</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="exit_valuation">Exit Valuation ($)</Label>
            <Input
              id="exit_valuation"
              type="number"
              step="1000"
              min="0"
              {...form.register('exit_valuation', { valueAsNumber: true })}
              placeholder="10000000"
            />
            {form.formState.errors.exit_valuation && (
              <p className="text-sm text-red-600">{form.formState.errors.exit_valuation.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="exit_date">Exit Date (Optional)</Label>
            <Input
              id="exit_date"
              type="date"
              {...form.register('exit_date')}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="assumptions">Assumptions (Optional)</Label>
            <Textarea
              id="assumptions"
              {...form.register('assumptions')}
              placeholder="Enter any assumptions or notes about this scenario..."
              rows={3}
            />
            <p className="text-xs text-gray-500">
              You can enter plain text or JSON format for structured data
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Scenario
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}